import React from 'react';
import { motion } from 'framer-motion';

export default function Privacy() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="pt-32 pb-20"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-6">Last updated: December 2024</p>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600">
              At DocumentAI.xyz, a service provided by Korcuda, we value your privacy and are committed to
              protecting your personal information. This Privacy Policy outlines how we collect, use, and safeguard
              the information you provide while using our document management service. By using our services, you
              agree to the practices described in this policy.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            <p className="text-gray-600 mb-4">We may collect the following types of information from you:</p>
            <ul className="list-disc pl-6 text-gray-600">
              <li><strong>Personal Information:</strong> Name, email address, and account credentials</li>
              <li><strong>Usage Data:</strong> Interaction logs, analytics, and preferences</li>
              <li><strong>Device Information:</strong> IP address, browser type, and operating system</li>
              <li><strong>Payment Details:</strong> Securely processed via third-party payment providers</li>
            </ul>
            <p className="text-gray-600">
              <strong>Note:</strong> We do not access or collect any data from the content of documents you upload or scan using our services. Your documents remain private and secure at all times.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">The information we collect is used to:</p>
            <ul className="list-disc pl-6 text-gray-600">
              <li>Provide, operate, and improve our services</li>
              <li>Analyze usage trends to enhance user experience</li>
              <li>Send service-related notifications and updates</li>
              <li>Respond to inquiries and provide customer support</li>
              <li>Comply with legal obligations and enforce our terms of service</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Sharing Your Information</h2>
            <p className="text-gray-600">
              We do not sell your personal information. We may share your data with trusted third parties to
              facilitate our services, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600">
              <li>Payment processors for handling transactions</li>
              <li>Cloud storage providers for document storage</li>
              <li>Analytics providers to understand usage trends</li>
              <li>Legal and regulatory authorities when required by law</li>
            </ul>
            <p className="text-gray-600">
              All third parties are required to adhere to strict confidentiality and data protection agreements.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-600">
              At DocumentAI.xyz, we implement industry-standard security measures, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600">
              <li>Encryption of data in transit (SSL/TLS) and at rest</li>
              <li>Access controls and multi-factor authentication</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Secure data backup protocols</li>
            </ul>
            <p className="text-gray-600">
              While we strive to protect your information, no method of transmission or storage is 100% secure. We
              encourage users to take personal precautions, such as choosing strong passwords and safeguarding
              their account credentials.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
            <p className="text-gray-600 mb-4">
              As a user, you have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-6 text-gray-600">
              <li>Access: Request a copy of your personal data</li>
              <li>Correction: Update or correct inaccurate information</li>
              <li>Deletion: Request deletion of your account and associated data</li>
              <li>Restriction: Limit the processing of your data under certain conditions</li>
              <li>Portability: Obtain a portable copy of your data</li>
            </ul>
            <p className="text-gray-600">
              To exercise these rights, contact us at <a href="mailto:kk1thebear@gmail.com" className="text-blue-600 hover:text-blue-700">kk1thebear@gmail.com</a>.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="text-gray-600">
              We use cookies and similar technologies to enhance your experience on our platform. These may include
              session cookies, which expire when you close your browser, and persistent cookies, which remain on
              your device until deleted. You can manage your cookie preferences through your browser settings.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Changes to This Privacy Policy</h2>
            <p className="text-gray-600">
              We may update this Privacy Policy periodically to reflect changes in our practices or legal
              requirements. Any updates will be posted on this page with a revised "Last updated" date. If
              significant changes are made, we will provide additional notice via email or within the app.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Contact Us</h2>
            <p className="text-gray-600">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-gray-600">
              <a href="mailto:kk1thebear@gmail.com" className="text-blue-600 hover:text-blue-700">
                kk1thebear@gmail.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </motion.div>
  );
}
